import React, { useState, useEffect } from 'react';
import { supabase } from '../../../utils/supabase';
import { useDarkMode } from '../../../context/DarkModeContext';
import { useToast } from '../../../context/ToastContext';
import { formatDateOnly } from '../../../utils/dateUtils';
import { useDraggable } from '@dnd-kit/core';
import { useAuthState } from '../../../hooks/useAuthState';

interface CareerCard {
  cardid: string;
  card_data: {
    title: string;
    description: string;
    type: string;
    custom_tags?: string[];
  };
  created_at: string;
}

interface DraggableCardProps {
  card: CareerCard;
  darkMode: boolean;
}

const DraggableCard: React.FC<DraggableCardProps> = ({ card, darkMode }) => {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `card-${card.cardid}`,
    data: {
      type: 'career_card',
      cardId: card.cardid,
      title: card.card_data.title,
      preview: card.card_data.description
    }
  });

  // Extract card content into a reusable component
  const CardContent = () => (
    <>
      <h3 className="font-medium mb-1">{card.card_data.title || 'Untitled Card'}</h3>
      <div className="text-sm text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-2">
        <span className={`px-2 py-0.5 rounded-full text-xs ${
          darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'
        }`}>
          {card.card_data.type || 'No Type'}
        </span>
        <span>•</span>
        <span>{formatDateOnly(card.created_at)}</span>
      </div>
      {card.card_data.custom_tags && card.card_data.custom_tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {card.card_data.custom_tags.map((tag, index) => (
            <span
              key={index}
              className={`text-xs px-2 py-0.5 rounded-full ${
                darkMode
                  ? 'bg-gray-600 text-gray-200'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </>
  );

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`p-4 border-b ${
        darkMode ? 'border-gray-700 bg-gray-800 hover:bg-gray-700' : 'border-gray-200 bg-white hover:bg-gray-50'
      } cursor-grab active:cursor-grabbing transition-colors duration-200 touch-none`}
    >
      <CardContent />
    </div>
  );
};

export const CardsTab: React.FC = () => {
  const { darkMode } = useDarkMode();
  const { showToast } = useToast();
  const [cards, setCards] = useState<CareerCard[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { user, isAuthenticated } = useAuthState();

  useEffect(() => {
    if (isAuthenticated) {
      fetchCards();
    }
  }, [isAuthenticated]);

  const fetchCards = async () => {
    try {
      setIsLoading(true);
      if (!user) return;

      const { data, error } = await supabase
        .from('career_cards')
        .select('cardid, card_data, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCards(data || []);
    } catch (error) {
      console.error('Error fetching career cards:', error);
      showToast('Failed to load career cards', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredCards = cards.filter(card =>
    card.card_data.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    card.card_data.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    card.card_data.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    card.card_data.custom_tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="h-full flex flex-col">
      {/* Search */}
      <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <input
          type="text"
          placeholder="Search career cards..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={`w-full px-3 py-2 rounded-md border ${
            darkMode
              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
          } focus:outline-none focus:ring-1 focus:ring-blue-500`}
        />
      </div>

      {/* Cards List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredCards.length > 0 ? (
          filteredCards.map(card => (
            <DraggableCard key={card.cardid} card={card} darkMode={darkMode} />
          ))
        ) : (
          <div className="p-4 text-center text-gray-500 dark:text-gray-400">
            {searchTerm ? 'No career cards found matching your search' : 'No career cards available'}
          </div>
        )}
      </div>
    </div>
  );
}; 